import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, X, Send, Wand2, MailCheck, FileText, Loader2 } from "lucide-react";
import { aiApi } from "@/lib/api";
import { APP } from "@/constants/testIds";
import { toast } from "sonner";

const QUICK_PROMPTS = [
  { icon: Wand2, label: "Draft JD", type: "jd", sample: "Draft a JD for a Senior ML Platform Engineer, remote, 5-8yr, Kubernetes + Ray + Python." },
  { icon: MailCheck, label: "Outreach", type: "outreach", sample: "Write a warm intro to Ava Patel about our Staff Frontend role at Northwind Robotics." },
  { icon: FileText, label: "Summarize", type: "summary", sample: "Interviewer 1: strong system design, weak on communication. Interviewer 2: good coding, hesitant on trade-offs. Interviewer 3: excellent culture fit, needs coaching on leadership." },
];

export default function AICopilotPanel({ open, onClose }) {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm your AI copilot powered by Claude 4.6. I can draft JDs, write outreach, summarize feedback, and rank candidates. What can I help with?" },
  ]);
  const [loading, setLoading] = useState(false);

  const send = async (typeOverride, sample) => {
    const text = sample || input;
    if (!text.trim()) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      let res;
      const t = typeOverride || "outreach";
      if (t === "jd") res = await aiApi.jd(text);
      else if (t === "summary") res = await aiApi.summarize(text);
      else res = await aiApi.outreach(text);
      const out = res.draft || res.summary || res.result || "(no response)";
      setMessages((m) => [...m, { role: "assistant", text: out, type: t }]);
    } catch (e) {
      toast.error("AI copilot error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid={APP.aiCopilotPanel}
      className="fixed right-0 top-0 h-screen w-full md:w-[420px] border-l border-border bg-background shadow-2xl z-50 flex flex-col"
      style={{ background: "linear-gradient(180deg, #ffffff 0%, rgba(67,56,202,0.03) 100%)" }}
    >
      <div className="h-16 border-b border-border flex items-center justify-between px-5 backdrop-blur-xl bg-background/80">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-[#4338CA] text-white grid place-items-center ai-pulse">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display font-bold tracking-tight text-sm">AI Copilot</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Claude Sonnet · Human-in-the-loop</div>
          </div>
        </div>
        <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md hover:bg-[#F4F4F5]">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] p-3 rounded-lg text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#0047AB] text-white" : "bg-white border border-border"}`}>
              {m.role === "assistant" && (
                <Badge className="mb-2 bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-50 font-mono text-[9px] tracking-wider">
                  <Sparkles className="h-2.5 w-2.5 mr-1" /> AI · Review before sending
                </Badge>
              )}
              {m.text}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-border p-3 rounded-lg text-sm flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-border p-4 bg-background">
        <div className="flex gap-2 mb-3 flex-wrap">
          {QUICK_PROMPTS.map((q) => {
            const Icon = q.icon;
            return (
              <button
                key={q.type}
                onClick={() => send(q.type, q.sample)}
                disabled={loading}
                className="text-xs px-2.5 py-1.5 rounded-md border border-border hover:border-[#4338CA] hover:text-[#4338CA] transition-colors flex items-center gap-1.5"
              >
                <Icon className="h-3 w-3" /> {q.label}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2">
          <textarea
            data-testid={APP.aiCopilotInput}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask the copilot… (Shift+Enter for newline)"
            rows={2}
            className="flex-1 resize-none px-3 py-2 rounded-md border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#4338CA]/20 focus:border-[#4338CA]"
          />
          <Button data-testid={APP.aiCopilotSend} onClick={() => send()} disabled={loading || !input.trim()} className="bg-[#4338CA] hover:bg-[#3730A3] h-auto">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
